/**
 * Overlapping initials avatars with a +N overflow, used for assignees and members.
 */

import { useRef, useState } from 'react';
import Popover from './Popover';
import UserAvatar from './UserAvatar';

export type AvatarStackPerson = {
  id: string | number;
  firstname?: string | null;
  surname?: string | null;
  name?: string | null;
  email?: string | null;
};

interface UserAvatarStackProps {
  people: readonly AvatarStackPerson[];
  /** How many faces to show before +N. */
  maxVisible?: number;
  size?: 'xs' | 'sm' | 'md';
  emptyLabel?: string;
  /** Total count when `people` is a preview (e.g. admins only). */
  totalCount?: number;
}

function displayName(person: AvatarStackPerson): string {
  return [person.firstname, person.surname].filter(Boolean).join(' ') || person.name?.trim() || person.email || '';
}

export default function UserAvatarStack({
  people,
  maxVisible = 4,
  size = 'sm',
  emptyLabel = '—',
  totalCount
}: UserAvatarStackProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const total = totalCount ?? people.length;

  const openPopover = (el: HTMLElement) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setAnchorEl(el);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setAnchorEl(null), 120);
  };

  if (total === 0) {
    return <span className="inline-flex items-center h-[28px] text-sm text-content-subtle">{emptyLabel}</span>;
  }

  const visible = people.slice(0, maxVisible);
  const overflow = Math.max(0, total - visible.length);
  const hiddenCount = Math.max(0, total - people.length);

  return (
    <>
      <div
        className="flex items-center w-fit"
        onMouseEnter={(event) => openPopover(event.currentTarget)}
        onMouseLeave={scheduleClose}
      >
        {visible.map((person, index) => (
          <div
            key={person.id}
            className={`relative rounded-full bg-white ring-2 ring-white ${index > 0 ? '-ml-1' : ''}`}
            style={{ zIndex: index + 1 }}
          >
            <UserAvatar
              firstname={person.firstname}
              surname={person.surname}
              name={person.name}
              email={person.email}
              size={size}
              showTooltip={false}
            />
          </div>
        ))}
        {overflow > 0 && <span className="ml-1.5 text-xs text-content-subtle">+{overflow}</span>}
      </div>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        placement="right"
        offset={[0, 8]}
        showArrow
        showBackdrop={false}
        onMouseEnter={() => {
          if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
          }
        }}
        onMouseLeave={scheduleClose}
        className="p-2 min-w-[12rem] max-w-[20rem] overflow-visible"
      >
        <ul className="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
          {people.map((person) => {
            const name = displayName(person);
            return (
              <li key={person.id} className="flex items-center gap-2 min-w-0">
                <UserAvatar
                  firstname={person.firstname}
                  surname={person.surname}
                  name={person.name}
                  email={person.email}
                  size="sm"
                  showTooltip={false}
                />
                <span className="text-sm text-content-default truncate">{name || 'Unknown'}</span>
              </li>
            );
          })}
        </ul>
        {hiddenCount > 0 && (
          <p className="mt-1.5 pl-1 text-xs text-content-subtle">
            +{hiddenCount} more {hiddenCount === 1 ? 'member' : 'members'}
          </p>
        )}
      </Popover>
    </>
  );
}
